import _ from 'underscore';

import {
  RECOMMENDATION_TYPES,
  ESTIMATE_TYPES,
  VISUAL_TYPES,
  VISUAL_TABLE_ENABLED_SCOPES,
} from './consts';

import { formatCost, formatPercent, formatBid, formatDecimal } from './formatter/index';
import { isPlural } from './util';

function buildActionGridDataForBidBoost({
  recommendation,
  i18n,
  currency,
}, useBid = false) {
  const {
    opportunities,
    bidCurrent,
    currentBidAdjustmentPercent,
    imprShareCurrent,
    costCurrent,
  } = recommendation;
  const formatCostWithi18n = _.partial(formatCost, _, i18n, currency);
  const formatPercentWithi18n = _.partial(formatPercent, _, i18n);
  const formatBidWithi18n = _.partial(formatBid, _, i18n, currency);
  const formatDecimalWithi18n = _.partial(formatDecimal, _, i18n);

  const bodies = _.map(opportunities, (opportunity) => {
    const newBidOrAdjustment = useBid ?
      formatBidWithi18n(opportunity.suggestedBid) :
      formatPercentWithi18n(opportunity.suggestedBidAdjustmentPercent / 100);
    const competitorImprShareIncrease = `${formatPercentWithi18n(opportunity.imprShareIncrease)} ${i18n.getString('Summary_Card_Estimate_Points')}`;
    const competitorTotalImprShare = formatPercentWithi18n(opportunity.totalImprShare);
    const competitorWeeklyCost = formatCostWithi18n(opportunity.weeklyCost);
    const competitorImprShare = formatPercentWithi18n(opportunity.competitorImprShare);
    const competitorClicksIncrease = formatDecimalWithi18n(opportunity.clicksIncrease);
    const competitorConversionsIncrease = formatDecimalWithi18n(opportunity.convIncrease);

    return [
      newBidOrAdjustment,
      competitorImprShareIncrease,
      competitorTotalImprShare,
      competitorClicksIncrease,
      competitorConversionsIncrease,
      competitorWeeklyCost,
      opportunity.competitorDomain,
      competitorImprShare,
    ];
  });
  const currentBidOrAdjustment = useBid ?
    formatBidWithi18n(bidCurrent) : formatPercentWithi18n(currentBidAdjustmentPercent / 100);
  const currentTotalImprShare = formatPercentWithi18n(imprShareCurrent);
  const currentWeeklyCost = formatCostWithi18n(costCurrent);
  const ZERO_PERCENT = `${formatPercentWithi18n(0)} ${i18n.getString('Summary_Card_Estimate_Points')}`;
  const ZERO = '0'; // no format should be needed

  bodies.push([
    currentBidOrAdjustment,
    ZERO_PERCENT,
    currentTotalImprShare,
    ZERO,
    ZERO,
    currentWeeklyCost,
    '',
    '']);
  return bodies;
}

function buildActionGridDataForNewLocation({
  recommendation,
  i18n,
  currency,
}) {
  const bodies = _.map(recommendation.opportunities, (opportunity) => {
    const recommendedLoc = opportunity.location;
    const searchQueryVolume = formatDecimal(opportunity.searchQueryVolume, i18n);
    const imprIncrease = formatDecimal(opportunity.impressionsIncrease, i18n);
    const costIncrease = formatCost(opportunity.costIncrease, i18n, currency);
    const clicksIncrease = formatDecimal(opportunity.clicksIncrease, i18n);

    return [
      recommendedLoc,
      searchQueryVolume,
      imprIncrease,
      clicksIncrease,
      costIncrease,
    ];
  });

  return bodies;
}

export const recommendationConfigs = {
  [RECOMMENDATION_TYPES.BUDGET]: {
    summaryTitle: 'Summary_Card_Title_Budget',
    summaryIconClasses: 'recommendation-budget-icon',
    summaryIconClassesVNext: 'Financial',
    detailDescription: 'Summary_Card_Detail_Description_Budget',
    detailPitching: 'Summary_Card_Detail_Pitching_Budget',
    detailHelpId: 'ext56677',
    sampleInsight: 'Summary_Card_Sample_Insight_Budget',
    sampleActionHeaders: [
      'Summary_Card_Action_Header_Buget_Increase',
      'Summary_Card_Action_Header_Est_Impression_Share',
    ],
    primaryEstimateType: ESTIMATE_TYPES.IMPRESSION_SHARE,
    secondaryEstimateType: ESTIMATE_TYPES.CLICKS,
    costEstimateType: ESTIMATE_TYPES.COST,
    isPrimaryFromToEnabled: true,
    visualType: VISUAL_TYPES.LINE_WEEKLY_CHART,
    recommendation: {
      actionTitle: 'Details_View_Modify_Budget',
      insightTitle: {
        key: 'Details_View_Budget_Insight',
        config: {
          useCampaignName: true,
        },
      },
      actionDataInForm: [{
        label: 'Details_View_Current_Budget',
        property: 'budgetCurrent',
        formatter: formatDecimal,
      }, {
        label: 'Details_View_Suggested_Budget',
        property: 'budgetSuggested',
        type: 'input',
        formatter: formatCost,
        placeHolder: 'Details_View_Suggested_Budget_Placeholder',
      }],
    },
  },
  [RECOMMENDATION_TYPES.COMPETITIVE_BID]: {
    summaryTitle: 'Summary_Card_Title_Competitive_Bid',
    summaryIconClasses: 'recommendation-bid-icon',
    summaryIconClassesVNext: 'WebConversion',
    detailDescription: 'Summary_Card_Detail_Description_Competitive_Bid',
    detailPitching: 'Summary_Card_Detail_Pitching_Competitive_Bid',
    detailHelpId: 'ext53092',
    sampleInsight: 'Summary_Card_Sample_Insight_Competitive_Bid',
    sampleActionHeaders: [
      'Summary_Card_Action_Header_Keyword',
      'Summary_Card_Action_Header_Suggested_Bid',
      'Summary_Card_Action_Header_Est_Impressions',
    ],
    primaryEstimateType: ESTIMATE_TYPES.IMPRESSION_SHARE,
    secondaryEstimateType: ESTIMATE_TYPES.CLICKS,
    costEstimateType: ESTIMATE_TYPES.COST,
    isPrimaryFromToEnabled: true,
    visualType: VISUAL_TYPES.LINE_COLUMN_CHART,
    recommendation: {
      insightTitle: {
        key: 'Details_View_Competitive_Bid_Insight',
        config: {
          useAdGroupName: true,
        },
      },
      actionTitle: 'Details_View_Increase_Keyword_Bid',
      actionGrid: {
        dataBuilder: _.partial(buildActionGridDataForBidBoost, _, true),
        columns: [{
          header: 'Details_View_Action_Header_New_Bid',
        }, {
          header: 'Details_View_Action_Header_Impression_Share_Change',
          isIncrease: true,
        }, {
          header: 'Details_View_Action_Header_Total_Impression_Share',
        }, {
          header: 'Details_View_Action_Header_Estimate_Clicks',
          isIncrease: true,
        }, {
          header: 'Details_View_Action_Header_Estimate_Conversions',
          isIncrease: true,
          autoHide: true,
        }, {
          header: 'Details_View_Action_Header_Weekly_Spend',
        }, {
          header: 'Details_View_Action_Header_Competitor_Domain',
          enableCheckedIcon: true,
        }, {
          header: 'Details_View_Action_Header_Competitor_Impresssion_Share',
        }],
        singleSelection: true,
        hasLastRowAsCurrent: true,
      },
    },
  },
  [RECOMMENDATION_TYPES.DEVICE_BID_BOOST]: {
    summaryTitle: 'Summary_Card_Title_Device_Bid_Boost',
    summaryIconClasses: 'recommendation-bid-icon',
    summaryIconClassesVNext: 'WebConversion',
    detailDescription: 'Summary_Card_Detail_Description_Device_Bid_Boost',
    detailPitching: 'Summary_Card_Detail_Pitching_Device_Bid_Boost',
    detailHelpId: 'ext51004',
    sampleInsight: 'Summary_Card_Sample_Insight_Device_Bid_Boost',
    sampleActionHeaders: [
      'Summary_Card_Action_Header_Device_Type',
      'Summary_Card_Action_Header_Increase_Bid',
      'Summary_Card_Action_Header_Est_Impression_Share',
    ],
    primaryEstimateType: ESTIMATE_TYPES.IMPRESSION_SHARE,
    secondaryEstimateType: ESTIMATE_TYPES.CLICKS,
    costEstimateType: ESTIMATE_TYPES.COST,
    isPrimaryFromToEnabled: true,
    visualType: VISUAL_TYPES.COLUMN_CHART,
    recommendation: {
      insightTitle: {
        key: 'Details_View_Device_Bid_Boost_Insight',
        config: {
          useLevelInComputingEntityName: true,
          compareWithCompetitor: true,
        },
      },
      actionTitle: {
        key: 'Details_View_Increase_Device_Bid_Boost',
        config: {
          useSegment: true,
        },
      },
      actionGrid: {
        dataBuilder: buildActionGridDataForBidBoost,
        columns: [{
          header: 'Details_View_Action_Header_New_Bid_Adjustment',
          isIncrease: true,
        }, {
          header: 'Details_View_Action_Header_Impression_Share_Change',
          isIncrease: true,
        }, {
          header: 'Details_View_Action_Header_Total_Impression_Share',
        }, {
          header: 'Details_View_Action_Header_Estimate_Clicks',
          isIncrease: true,
        }, {
          header: 'Details_View_Action_Header_Estimate_Conversions',
          isIncrease: true,
          autoHide: true,
        }, {
          header: 'Details_View_Action_Header_Weekly_Spend',
        }, {
          header: 'Details_View_Action_Header_Competitor_Domain',
          enableCheckedIcon: true,
        }, {
          header: 'Details_View_Action_Header_Competitor_Impresssion_Share',
        }],
        singleSelection: true,
        hasLastRowAsCurrent: true,
      },
    },
  },
  [RECOMMENDATION_TYPES.LOCATION_BID_BOOST]: {
    summaryTitle: 'Summary_Card_Title_Location_Bid_Boost',
    summaryIconClasses: 'recommendation-bid-icon',
    summaryIconClassesVNext: 'WebConversion',
    detailDescription: 'Summary_Card_Detail_Description_Location_Bid_Boost',
    detailPitching: 'Summary_Card_Detail_Pitching_Location_Bid_Boost',
    detailHelpId: 'ext51100',
    sampleInsight: 'Summary_Card_Sample_Insight_Location_Bid_Boost',
    sampleActionHeaders: [
      'Summary_Card_Action_Header_Location',
      'Summary_Card_Action_Header_Increase_Bid',
      'Summary_Card_Action_Header_Est_Impression_Share',
    ],
    primaryEstimateType: ESTIMATE_TYPES.IMPRESSION_SHARE,
    secondaryEstimateType: ESTIMATE_TYPES.CLICKS,
    costEstimateType: ESTIMATE_TYPES.COST,
    isPrimaryFromToEnabled: true,
    visualType: VISUAL_TYPES.COLUMN_CHART,
    recommendation: {
      insightTitle: {
        key: 'Details_View_Location_Bid_Boost_Insight',
        config: {
          useLevelInComputingEntityName: true,
          compareWithCompetitor: true,
        },
      },
      actionTitle: 'Details_View_Increase_Location_Bid_Boost',
      actionGrid: {
        dataBuilder: buildActionGridDataForBidBoost,
        columns: [{
          header: 'Details_View_Action_Header_New_Bid_Adjustment',
          isIncrease: true,
        }, {
          header: 'Details_View_Action_Header_Impression_Share_Change',
          isIncrease: true,
        }, {
          header: 'Details_View_Action_Header_Total_Impression_Share',
        }, {
          header: 'Details_View_Action_Header_Estimate_Clicks',
          isIncrease: true,
        }, {
          header: 'Details_View_Action_Header_Estimate_Conversions',
          isIncrease: true,
          autoHide: true,
        }, {
          header: 'Details_View_Action_Header_Weekly_Spend',
        }, {
          header: 'Details_View_Action_Header_Competitor_Domain',
          enableCheckedIcon: true,
        }, {
          header: 'Details_View_Action_Header_Competitor_Impresssion_Share',
        }],
        singleSelection: true,
        hasLastRowAsCurrent: true,
      },
      postprocessor(data, recommendation, i18n) {
        if (_.isUndefined(recommendation.targetGroupId) ||
          _.isNull(recommendation.targetGroupId) ||
          recommendation.targetGroupId === '') {
          /* eslint-disable no-param-reassign */
          data.actionTitle = i18n.getString('Details_View_Target_Location_Bid_Boost');
          data.actionDataInGrid.hasLastRowAsCurrent = false;
          data.actionDataInGrid.bodies.pop();
          data.actionTitleExtraForm = [{
            label: i18n.getString('Details_View_Action_Header_Target_Location'),
            property: 'segment',
            value: recommendation.segment.toString(),
          }];
          /* eslint-enable no-param-reassign */
        }
      },
    },
  },
  // deprecated in Oct 2023
  [RECOMMENDATION_TYPES.NEW_KEYWORD]: {
    summaryTitle: 'Summary_Card_Title_New_Keyword',
    summaryIconClasses: 'recommendation-keyword-icon',
    summaryIconClassesVNext: 'SearchCampaigns',
    detailDescription: 'Summary_Card_Detail_Description_New_Keyword',
    detailPitching: 'Summary_Card_Detail_Pitching_New_Keyword',
    detailHelpId: 'ext51010',
    sampleInsight: 'Summary_Card_Sample_Insight_New_Keyword',
    primaryEstimateType: ESTIMATE_TYPES.IMPRESSIONS,
    secondaryEstimateType: ESTIMATE_TYPES.CLICKS,
    costEstimateType: ESTIMATE_TYPES.COST,
    isPrimaryFromToEnabled: true,
    visualType: VISUAL_TYPES.TABLE,
    visualTableHeaders: [
      'Summary_Card_Action_Header_Suggested_Keyword',
      /* 'Summary_Card_Action_Header_Monthly_Search_Volume',
      //bug 1457951: disable until est. weekly search volume is available */
      'Summary_Card_Action_Header_Suggested_Bid',
      'Summary_Card_Action_Header_Est_Impressions',
    ],
    recommendation: {
      visualType: VISUAL_TYPES.SEARCH_TERMS_CHART,
      actionTitle: 'Details_View_Add_Keyword',
      insightTitle: {
        key: 'Details_View_Keyword_Insight',
        config: {
          useAdGroupName: true,
        },
      },
      visualData: [{
        label: 'Details_View_Insight_Suggested_Keyword',
        property: 'suggestedKeyword',
      }, {
        label: 'Details_View_Insight_Search_Query',
        property: 'monthlyQueryVolume',
        formatter: formatDecimal,
      }],
      actionDataInForm: [{
        label: 'Details_View_Keyword',
        property: 'suggestedKeyword',
      }, {
        label: 'Details_View_Suggested_Keyword_Bid',
        type: 'input',
        property: 'suggestedBid',
        formatter: formatBid,
        placeHolder: 'Details_View_Suggested_Keyword_Bid_Placeholder',
      }, {
        label: 'Details_View_Match_Type',
        type: 'select',
        property: 'matchType',
        enum: [{
          value: '1',
          text: 'Details_View_Match_Type_Exact',
        }, {
          value: '2',
          text: 'Details_View_Match_Type_Phrase',
        }, {
          value: '3',
          text: 'Details_View_Match_Type_Broad',
        }],
      }],
    },
  },
  [RECOMMENDATION_TYPES.NEW_LOCATION]: {
    summaryTitle: 'Summary_Card_Title_New_Location',
    summaryIconClasses: 'recommendation-targeting-icon',
    summaryIconClassesVNext: 'POI',
    detailDescription: 'Summary_Card_Detail_Description_New_Location',
    detailPitching: 'Summary_Card_Detail_Pitching_New_Location',
    detailHelpId: 'ext51100',
    sampleInsight: 'Summary_Card_Sample_Insight_New_Location',
    sampleActionHeaders: [
      'Summary_Card_Action_Header_Target_Location',
      'Summary_Card_Action_Header_Est_Impressions',
    ],
    primaryEstimateType: ESTIMATE_TYPES.IMPRESSIONS,
    secondaryEstimateType: ESTIMATE_TYPES.CLICKS,
    costEstimateType: ESTIMATE_TYPES.COST,
    isPrimaryFromToEnabled: true,
    visualType: VISUAL_TYPES.MAP,
    recommendation: {
      actionTitle: 'Details_View_Add_New_Location',
      insightTitle: {
        key: 'Details_View_New_Location_Insight',
        config: {
          useCampaignName: true,
          useOpportunityCount: true,
        },
      },
      opportunities: [{}],
      actionGrid: {
        dataBuilder: buildActionGridDataForNewLocation,
        columns: [{
          header: 'Details_View_Action_Header_Recommended_Locations',
        }, {
          header: 'Details_View_Action_Header_Query_Volume',
        }, {
          header: 'Details_View_Action_Header_Estimate_Impressions',
          isIncrease: true,
        }, {
          header: 'Details_View_Action_Header_Estimate_Clicks',
          isIncrease: true,
        }, {
          header: 'Details_View_Action_Header_Estimate_Spend',
          isIncrease: true,
        }],
        singleSelection: false,
        helpTopics: [
          'pop_BA_CompetitionTab_RecommendedLocations',
          'pop_BA_CompetitionTab_Searchvolume',
          'pop_BA_CompetitionTab_EstImpr',
        ],
      },
    },
  },
  [RECOMMENDATION_TYPES.SYNDICATION]: {
    summaryTitle: 'Summary_Card_Title_Syndication',
    summaryIconClasses: 'recommendation-targeting-icon',
    summaryIconClassesVNext: 'POI',
    detailDescription: 'Summary_Card_Detail_Description_Syndication',
    detailPitching: 'Summary_Card_Detail_Pitching_Syndication',
    detailHelpId: 'ext52031',
    sampleInsight: 'Summary_Card_Sample_Insight_Syndication',
    primaryEstimateType: ESTIMATE_TYPES.IMPRESSIONS,
    secondaryEstimateType: ESTIMATE_TYPES.CLICKS,
    costEstimateType: ESTIMATE_TYPES.COST,
    isPrimaryFromToEnabled: true,
    visualType: VISUAL_TYPES.TABLE,
    visualTableHeaders: [
      'Summary_Card_Action_Header_Campaign',
      'Summary_Card_Action_Header_AdGroup',
      'Summary_Card_Action_Header_Adoption_Percentage',
      'Summary_Card_Action_Header_Est_Change_Impressions',
    ],
    recommendation: {
      insightTitle: {
        key: 'Summary_Card_Sample_Insight_Syndication',
        config: {
          useAdGroupName: true,
        },
      },
      visualData: [{
        label: 'Details_View_Syndication_Insight_Adoption_Label',
        useProgressBar: true,
        property: 'adoptionPercent',
        formatter: (propertyValue, i18n) => formatPercent(propertyValue / 100, i18n),
      }],
      actionDataInForm: [{
        text: 'Details_View_Syndication_Action_Text',
        helpText: 'Details_View_Syndication_Action_Help_Text',
        helpQuery: 'ext56674',
        type: 'info',
      }],
    },
  },
  [RECOMMENDATION_TYPES.NEW_KEYWORD_OPPORTUNITY]: {
    summaryTitle: 'Summary_Card_Title_New_Keyword_Opportunity',
    summaryIconClasses: 'recommendation-keyword-icon',
    summaryIconClassesVNext: 'SearchCampaigns',
    detailDescription: 'Summary_Card_Detail_Description_New_Keyword_Opportunity',
    detailPitching: 'Summary_Card_Detail_Pitching_New_Keyword_Opportunity',
    detailHelpId: 'ext51010',
    visualType: VISUAL_TYPES.TABLE,
    visualTableScope: VISUAL_TABLE_ENABLED_SCOPES.ADGROUP,
    visualTableHeaders: [
      'Summary_Card_Action_Header_Recommended_Keyword',
      'Summary_Card_Action_Header_Recommended_From',
    ],
  },
  [RECOMMENDATION_TYPES.TRENDING_QUERY]: {
    summaryTitle: 'Summary_Card_Title_Trending_Query',
    summaryIconClasses: 'recommendation-keyword-icon',
    summaryIconClassesVNext: 'SearchCampaigns',
    detailDescription: 'Summary_Card_Detail_Description_Trending_Query',
    detailPitching: 'Summary_Card_Detail_Pitching_Trending_Query',
    detailHelpId: 'ext51010',
    visualType: VISUAL_TYPES.TABLE,
    visualTableScope: VISUAL_TABLE_ENABLED_SCOPES.ADGROUP,
    visualTableHeaders: [
      'Summary_Card_Action_Header_Recommended_Keyword',
      'Summary_Card_Action_Header_Recommended_Est_Search_Volume',
    ],
  },
  [RECOMMENDATION_TYPES.DYNAMIC_SEARCH_ADS]: {
    summaryTitle: 'Summary_Card_Title_Dynamic_Search_Ads',
    summaryIconClasses: 'recommendation-ads-icon',
    summaryIconClassesVNext: 'Chart',
    detailDescription: 'Summary_Card_Detail_Description_Dynamic_Search_Ads',
    detailPitching: 'Summary_Card_Detail_Pitching_Dynamic_Search_Ads',
    detailHelpId: 'ext56794',
    isPrimaryFromToEnabled: false,
    primaryEstimateType: ESTIMATE_TYPES.WEEKLY_SEARCHES,
    primaryIncreaseNullable: true,
    visualType: VISUAL_TYPES.TABLE,
    visualTableScope: VISUAL_TABLE_ENABLED_SCOPES.ACCOUNT,
    visualTableHeaders: [
      'Summary_Card_Action_Header_Example_Of_Searches',
    ],
  },

  [RECOMMENDATION_TYPES.GOOGLE_IMPORT]: {
    summaryTitle: 'Summary_Card_Title_Google_Import',
    summaryIconClasses: 'recommendation-keyword-icon',
    summaryIconClassesVNext: 'SearchCampaigns',
    detailDescription: 'Summary_Card_Detail_Description_Google_Import',
    detailPitching: 'Summary_Card_Detail_Pitching_Google_Import',
    detailHelpId: 'ext51050',
    primaryEstimateType: ESTIMATE_TYPES.CLICKS,
    secondaryEstimateType: ESTIMATE_TYPES.IMPRESSIONS,
    isPrimaryFromToEnabled: true,
    visualType: VISUAL_TYPES.TABLE,
    visualTableHeaders: [],
  },
  [RECOMMENDATION_TYPES.GOOGLE_IMPORT_SCHEDULED]: {
    summaryTitle: 'Summary_Card_Title_Google_Import_Scheduled',
    summaryIconClasses: 'recommendation-keyword-icon',
    summaryIconClassesVNext: 'SearchCampaigns',
    detailDescription: 'Summary_Card_Detail_Description_Google_Import_Scheduled',
    detailPitching: 'Summary_Card_Detail_Pitching_Google_Import_Scheduled',
    detailHelpId: 'ext50851',
    primaryEstimateType: ESTIMATE_TYPES.CLICKS,
    secondaryEstimateType: ESTIMATE_TYPES.IMPRESSIONS,
    isPrimaryFromToEnabled: true,
    visualType: VISUAL_TYPES.TABLE,
    visualTableHeaders: [],
  },
  [RECOMMENDATION_TYPES.SITE_LINK]: {
    summaryTitle: 'Summary_Card_Title_Site_Link',
    summaryIconClasses: 'recommendation-extension-icon',
    summaryIconClassesVNext: 'Add',
    detailDescription: 'Summary_Card_Detail_Description_Site_Link',
    detailPitching: 'Summary_Card_Detail_Pitching_Site_Link',
    detailHelpId: 'ext56823',
    primaryEstimateType: ESTIMATE_TYPES.CTR,
    secondaryEstimateType: ESTIMATE_TYPES.CLICKS,
    costEstimateType: ESTIMATE_TYPES.COST,
    isPrimaryFromToEnabled: true,
    visualType: VISUAL_TYPES.TABLE,
    visualTableScope: VISUAL_TABLE_ENABLED_SCOPES.ACCOUNT,
    visualTableHeaders: [
      'Summary_Card_Action_Header_Campaign',
      'Summary_Card_Action_Header_Est_CTR',
    ],
  },
  [RECOMMENDATION_TYPES.BUDGET_OPPORTUNITY]: {
    summaryTitle: 'Summary_Card_Title_Budget_Opportunity',
    summaryIconClasses: 'recommendation-budget-icon',
    summaryIconClassesVNext: 'Financial',
    detailDescription: 'Summary_Card_Detail_Description_Budget_Opportunity',
    detailPitching: 'Summary_Card_Detail_Pitching_Budget_Opportunity',
    detailHelpId: 'ext53099',
    isPrimaryFromToEnabled: true,
    visualType: VISUAL_TYPES.TABLE,
    visualTableScope: VISUAL_TABLE_ENABLED_SCOPES.ACCOUNT,
    visualTableHeaders: [
      'Summary_Card_Action_Header_Campaign',
    ],
  },
  [RECOMMENDATION_TYPES.ADJUST_SHARED_BUDGET_OPPORTUNITY]: {
    summaryTitle: 'Summary_Card_Title_Adjust_Shared_Budget_Opportunity',
    summaryIconClasses: 'recommendation-budget-icon',
    summaryIconClassesVNext: 'Financial',
    detailDescription: 'Summary_Card_Detail_Description_Adjust_Shared_Budget_Opportunity',
    detailPitching: 'Summary_Card_Detail_Pitching_Adjust_Shared_Budget_Opportunity',
    detailHelpId: 'ext53099', // todo
    isPrimaryFromToEnabled: true,
    visualType: VISUAL_TYPES.TABLE,
    visualTableScope: VISUAL_TABLE_ENABLED_SCOPES.ACCOUNT,
    visualTableHeaders: [
      'Summary_Card_Action_Header_Shared_Budget',
    ],
  },
  [RECOMMENDATION_TYPES.CALLOUT]: {
    summaryTitle: 'Summary_Card_Title_Callout',
    summaryIconClasses: 'recommendation-extension-icon',
    summaryIconClassesVNext: 'Add',
    detailDescription: 'Summary_Card_Detail_Description_Callout',
    detailPitching: 'Summary_Card_Detail_Pitching_Callout',
    detailHelpId: 'ext56766',
    primaryEstimateType: ESTIMATE_TYPES.CTR,
    secondaryEstimateType: ESTIMATE_TYPES.CLICKS,
    costEstimateType: ESTIMATE_TYPES.COST,
    isPrimaryFromToEnabled: true,
    visualType: VISUAL_TYPES.TABLE,
    visualTableScope: VISUAL_TABLE_ENABLED_SCOPES.ACCOUNT,
    visualTableHeaders: [
      'Summary_Card_Action_Header_Campaign',
      'Summary_Card_Action_Header_Est_CTR',
    ],
  },
  [RECOMMENDATION_TYPES.REPAIR_ADS]: {
    summaryTitle: 'Summary_Card_Title_Repair_Ads',
    summaryIconClasses: 'recommendation-repair-icon',
    summaryIconClassesVNext: 'Error',
    detailDescription: 'Summary_Card_Detail_Description_Repair_Ads',
    detailPitching: 'Summary_Card_Detail_Pitching_Repair_Ads_Temp',
    detailHelpId: 'app54696',
    isPrimaryFromToEnabled: true,
    visualType: VISUAL_TYPES.TABLE,
    visualTableScope: VISUAL_TABLE_ENABLED_SCOPES.CAMPAIGN,
    visualTableHeaders: [
      'Summary_Card_Action_Header_Ad_Group_Without_Ads',
    ],
  },
  [RECOMMENDATION_TYPES.FIX_AD_TEXT]: {
    summaryTitle: 'Summary_Card_Title_Fix_Ad_Text',
    summaryIconClasses: 'recommendation-repair-icon',
    summaryIconClassesVNext: 'Error',
    detailDescription: 'Summary_Card_Detail_Description_Fix_Ad_Text',
    detailPitching: 'Summary_Card_Detail_Pitching_Fix_Ad_Text',
    detailHelpId: 'app54725',
    isPrimaryFromToEnabled: false,
    visualType: VISUAL_TYPES.TABLE,
    visualTableScope: VISUAL_TABLE_ENABLED_SCOPES.CAMPAIGN,
    visualTableHeaders: [
      'Summary_Card_Action_Header_Top_Policies',
      'Summary_Card_Action_Header_Rejected_Ads',
      'Summary_Card_Action_Header_Rejected_Keywords',
    ],
    isEstimate: false,
  },
  [RECOMMENDATION_TYPES.FIX_CONVERSION_TRACKING_OPPORTUNITY]: {
    summaryTitle: 'Summary_Card_Title_Fix_Conversion_Tracking',
    summaryIconClasses: 'recommendation-repair-icon',
    summaryIconClassesVNext: 'Error',
    detailDescription: 'Summary_Card_Detail_Description_Fix_Conversion_Tracking',
    detailPitching: 'Summary_Card_Detail_Pitching_Fix_Conversion_Tracking',
    detailHelpId: 'ext56951',
    isPrimaryFromToEnabled: false,
    isEstimate: false,
    visualType: VISUAL_TYPES.TABLE,
    visualTableHeaders: [],
  },
  [RECOMMENDATION_TYPES.REPAIR_KEYWORD]: {
    summaryTitle: 'Summary_Card_Title_Repair_Keywords',
    summaryIconClasses: 'recommendation-repair-icon',
    summaryIconClassesVNext: 'Error',
    detailDescription: 'Summary_Card_Detail_Description_Repair_Keywords',
    detailPitching: 'Summary_Card_Detail_Pitching_Repair_Keywords_Temp',
    detailHelpId: 'app54697',
    isPrimaryFromToEnabled: true,
    visualType: VISUAL_TYPES.TABLE,
    visualTableScope: VISUAL_TABLE_ENABLED_SCOPES.CAMPAIGN,
    visualTableHeaders: [
      'Summary_Card_Action_Header_Ad_Group_Without_Keywords',
    ],
  },
  [RECOMMENDATION_TYPES.BROAD_MATCH_KEYWORD]: {
    summaryTitle: 'Summary_Card_Title_Broad_Match_Keywords',
    summaryIconClasses: 'recommendation-keyword-icon',
    summaryIconClassesVNext: 'SearchCampaigns',
    detailDescription: 'Summary_Card_Detail_Description_Broad_Match_Keyword',
    detailPitching: 'Summary_Card_Detail_Pitching_Broad_Match_Keywords',
    detailHelpId: 'ext56911',
    visualType: VISUAL_TYPES.TABLE,
    visualTableScope: VISUAL_TABLE_ENABLED_SCOPES.ADGROUP,
    visualTableHeaders: [
      'Summary_Card_Action_Header_Broad_Match_Keyword',
    ],
  },
  [RECOMMENDATION_TYPES.REMOVE_CONFLICTING_NEGATIVE_KEYWORD]: {
    summaryTitle: 'Summary_Card_Title_Remove_Conflicting_Negative_Keyword',
    summaryIconClasses: 'recommendation-keyword-icon',
    summaryIconClassesVNext: 'SearchCampaigns',
    detailDescription: 'Summary_Card_Detail_Description_Remove_Conflicting_Negative_Keyword',
    detailPitching: 'Summary_Card_Detail_Pitching_Remove_Conflicting_Negative_Keyword',
    detailHelpId: 'ext51014',
    isPrimaryFromToEnabled: true,
    visualType: VISUAL_TYPES.TABLE,
    visualTableScope: VISUAL_TABLE_ENABLED_SCOPES.ADGROUP,
    visualTableHeaders: [
      'Summary_Card_Action_Header_Use_Remove_Negative_Keywords',
      'Summary_Card_Action_Header_Blocked_Keywords',
    ],
  },
  [RECOMMENDATION_TYPES.RESPONSIVE_SEARCH_ADS]: {
    summaryTitle: 'Summary_Card_Title_Responsive_Search_Ads',
    summaryIconClasses: 'recommendation-ads-icon',
    summaryIconClassesVNext: 'Chart',
    detailDescription: 'Summary_Card_Detail_Description_Responsive_Search_Ads',
    detailPitching: 'Summary_Card_Detail_Pitching_Responsive_Search_Ads',
    detailHelpId: 'ext60037',
    isPrimaryFromToEnabled: true,
    visualType: VISUAL_TYPES.TABLE,
    visualTableScope: VISUAL_TABLE_ENABLED_SCOPES.ADGROUP,
    visualTableHeaders: [
      'Summary_Card_Action_Header_Ad_Group_Without_Responsive_Search_Ads',
    ],
  },
  [RECOMMENDATION_TYPES.REALLOCATE_BUDGET]: {
    summaryTitle: 'Summary_Card_Title_Reallocate_Budget_Opportunity',
    summaryIconClasses: 'recommendation-budget-icon',
    summaryIconClassesVNext: 'Financial',
    detailDescription: 'Summary_Card_Detail_Description_Reallocate_Budget_Opportunity',
    detailPitching: 'Summary_Card_Detail_Pitching_Reallocate_Budget_Opportunity',
    detailHelpId: 'ext53099',
    primaryEstimateType: ESTIMATE_TYPES.CLICKS,
    primaryIncreaseNullable: true,
    costEstimateType: ESTIMATE_TYPES.COST,
    isPrimaryFromToEnabled: true,
    visualType: VISUAL_TYPES.TABLE,
    visualTableScope: VISUAL_TABLE_ENABLED_SCOPES.CAMPAIGN,
    visualTableHeaders: [
      'Summary_Card_Action_Header_SuggestedBudget',
      'Summary_Card_Action_Header_FromCampaign',
      'Summary_Card_Action_Header_ToCampaign',
      'Summary_Card_Action_Header_Est_Clicks',
    ],
  },
  [RECOMMENDATION_TYPES.FIX_AD_DESTINATION]: {
    summaryTitle: 'Summary_Card_Title_Fix_Ad_Destination_Opportunity',
    summaryIconClasses: 'recommendation-repair-icon',
    summaryIconClassesVNext: 'Error',
    detailDescription: 'Summary_Card_Detail_Description_Fix_Ad_Destination_Opportunity',
    detailPitching: 'Summary_Card_Detail_Pitching_Fix_Ad_Destination_Opportunity',
    detailHelpId: '2128217',
    detailLinkType: 'fwdLink',
    visualType: VISUAL_TYPES.TABLE,
    visualTableScope: VISUAL_TABLE_ENABLED_SCOPES.ADGROUP,
    visualTableHeaders: [
      'Summary_Card_Action_Header_LandingPageUrl',
    ],
    isPrimaryFromToEnabled: false,
    isEstimate: false,
  },
  [RECOMMENDATION_TYPES.SETUP_CONVERSION_TRACKING_OPPORTUNITY]: {
    summaryTitle: 'Summary_Card_Title_Setup_Conversion_Tracking_Opportunity_UnCreated',
    summaryIconClasses: 'recommendation-shopping-cart-icon',
    summaryIconClassesVNext: 'ShoppingCart',
    detailDescription: 'Summary_Card_Detail_Description_Setup_Conversion_Tracking_Opportunity_UnCreated',
    detailPitching: 'Summary_Card_Detail_Pitching_Setup_Conversion_Tracking_Opportunity_UnCreated',
    detailHelpId: 'ext56680',
    isPrimaryFromToEnabled: false,
    isEstimate: false,
    visualType: VISUAL_TYPES.TABLE,
    visualTableHeaders: [],
    isDynamicText: true,
  },
  [RECOMMENDATION_TYPES.FIX_CONVERSION_GOAL_SETTINGS]: {
    summaryTitle: 'Summary_Card_Title_Fix_Conversion_Goal_Settings',
    summaryIconClasses: 'recommendation-repair-icon',
    summaryIconClassesVNext: 'Error',
    detailDescription: 'Summary_Card_Detail_Description_Fix_Conversion_Goal_Settings',
    detailPitching: 'Summary_Card_Detail_Pitching_Fix_Conversion_Goal_Settings',
    detailHelpId: '56951',
    primaryEstimateType: ESTIMATE_TYPES.MISSING_CONVERSIONS,
    visualType: VISUAL_TYPES.TABLE,
    visualTableHeaders: [],
    isPrimaryFromToEnabled: false,
  },
  [RECOMMENDATION_TYPES.CREATE_CONVERSION_GOAL]: {
    summaryTitle: _TL_('Create conversion goal'),
    summaryIconClasses: 'recommendation-shopping-cart-icon',
    summaryIconClassesVNext: 'ShoppingCart',
    detailDescription: _TL_('Track more about your conversion performance.'),
    detailPitching: _TL_("This action is recommended because UET is enabled, but you don't have any recent conversions. Create a conversion goal with a converted destination URL or custom events to help track more conversion data."),
    detailHelpId: '56689',
    primaryEstimateType: ESTIMATE_TYPES.MISSING_CONVERSIONS,
    visualType: VISUAL_TYPES.TABLE,
    visualTableHeaders: [],
    isPrimaryFromToEnabled: false,
    useNewI18n: true,
  },
  [RECOMMENDATION_TYPES.FACEBOOK_IMPORT]: {
    summaryTitle: 'Summary_Card_Title_Facebook_Import',
    summaryIconClasses: 'recommendation-keyword-icon',
    summaryIconClassesVNext: 'SearchCampaigns',
    detailDescription: 'Summary_Card_Detail_Description_Facebook_Import',
    detailPitching: 'Summary_Card_Detail_Pitching_Facebook_Import',
    detailHelpId: '60078',
    primaryEstimateType: ESTIMATE_TYPES.IMPRESSIONS,
    secondaryEstimateType: ESTIMATE_TYPES.CLICKS,
    isPrimaryFromToEnabled: true,
    visualType: VISUAL_TYPES.TABLE,
    visualTableHeaders: [],
  },
  [RECOMMENDATION_TYPES.MULTIMEDIA_ADS]: {
    summaryTitle: 'Summary_Card_Title_Multi_Media_Ads',
    summaryIconClasses: 'recommendation-ads-icon',
    summaryIconClassesVNext: 'Chart',
    detailDescription: 'Summary_Card_Detail_Description_Multi_Media_Ads',
    detailPitching: 'Summary_Card_Detail_Pitching_Multi_Media_Ads',
    detailHelpId: '60107',
    isPrimaryFromToEnabled: true,
    visualType: VISUAL_TYPES.TABLE,
    visualTableScope: VISUAL_TABLE_ENABLED_SCOPES.ADGROUP,
    visualTableHeaders: [
      'Summary_Card_Action_Header_Ad_Group_Without_Multi_Media_Ads',
    ],
  },
  [RECOMMENDATION_TYPES.IMAGE_EXTENSION]: {
    summaryTitle: 'Summary_Card_Title_Image_Extension',
    summaryIconClasses: 'recommendation-ads-icon',
    summaryIconClassesVNext: 'Add',
    detailDescription: 'Summary_Card_Detail_Description_Image_Extension',
    detailPitching: 'Summary_Card_Detail_Pitching_Image_Extension',
    detailHelpId: '56674',
    primaryEstimateType: ESTIMATE_TYPES.CLICKS,
    primaryIncreaseNullable: true,
    costEstimateType: ESTIMATE_TYPES.COST,
    isPrimaryFromToEnabled: true,
    visualType: VISUAL_TYPES.TABLE,
    visualTableScope: VISUAL_TABLE_ENABLED_SCOPES.CAMPAIGN,
    visualTableHeaders: [
      'Summary_Card_Action_Header_Campaign',
      'Summary_Card_Action_Header_Est_Clicks',
    ],
  },
  [RECOMMENDATION_TYPES.REPAIR_MISSING_KEYWORD_PARAMS]: {
    summaryTitle: _TL_('Fix missing keyword parameters issue'),
    summaryIconClasses: 'recommendation-repair-icon',
    summaryIconClassesVNext: 'Error',
    detailDescription: _TL_('Increase ad impressions and improve your click-through rate by setting up appropriate values for missing keyword parameters.'),
    detailPitching: _TL_('Setting values for your keyword parameters could enhance your search ads performance.'),
    detailHelpId: 'app54450',
    primaryEstimateType: ESTIMATE_TYPES.IMPRESSIONS,
    secondaryEstimateType: ESTIMATE_TYPES.CLICKS,
    primaryIncreaseNullable: true,
    isPrimaryFromToEnabled: true,
    visualType: VISUAL_TYPES.TABLE,
    visualTableScope: VISUAL_TABLE_ENABLED_SCOPES.CAMPAIGN,
    visualTableHeaders: [
      _TL_('Keywords'),
      _TL_('Weekly est. impressions growth'),
    ],
    useNewI18n: true,
  },
  [RECOMMENDATION_TYPES.FIX_NO_IMPRESSION_BID]: {
    summaryTitle: _TL_('Increase your keyword bids'),
    summaryIconClasses: 'recommendation-bid-icon',
    summaryIconClassesVNext: 'WebConversion',
    detailDescription: _TL_('Get more impressions in your enhanced CPC campaign by increasing bids.'),
    detailPitching: _TL_('Recommended because these keywords haven\'t received impressions in the past 2 weeks due to low keyword bids.'),
    detailHelpId: '51018',
    primaryEstimateType: ESTIMATE_TYPES.IMPRESSIONS,
    primaryIncreaseNullable: true,
    isPrimaryFromToEnabled: true,
    visualType: VISUAL_TYPES.TABLE,
    visualTableScope: VISUAL_TABLE_ENABLED_SCOPES.CAMPAIGN,
    visualTableHeaders: [
      _TL_('Keywords'),
      _TL_('Suggested bid'),
      _TL_('Weekly est. impr.'),
    ],
    useNewI18n: true,
  },
  [RECOMMENDATION_TYPES.IMPROVE_RESPONSIVE_SEARCH_ADS]: {
    summaryTitle: _TL_('Improve your responsive search ads'),
    summaryIconClasses: 'recommendation-ads-icon',
    summaryIconClassesVNext: 'Chart',
    detailDescription: _TL_('Get more clicks on your responsive search ads by improving your headlines and descriptions'),
    detailPitching: _TL_('Recommended because some of your responsive search ads don\'t have sufficient headlines or descriptions.'),
    detailHelpId: 'ext60037',
    primaryEstimateType: ESTIMATE_TYPES.CLICKS,
    secondaryEstimateType: ESTIMATE_TYPES.IMPRESSIONS,
    primaryIncreaseNullable: true,
    isPrimaryFromToEnabled: true,
    visualType: VISUAL_TYPES.TABLE,
    visualTableScope: VISUAL_TABLE_ENABLED_SCOPES.CAMPAIGN,
    visualTableHeaders: [],
    useNewI18n: true,
  },
  [RECOMMENDATION_TYPES.REPAIR_UNTARGETED_OFFER]: {
    summaryTitle: _TL_('Target all eligible shopping products'),
    summaryIconClasses: 'recommendation-shopping-cart-icon',
    summaryIconClassesVNext: 'ShoppingCart',
    detailDescription: _TL_('Get more conversion value from your shopping products by advertising with a catch-all campaign.'),
    detailPitching: _TL_('Recommended because you have untargeted online products in your shopping feed.'),
    detailHelpId: 'ext56483',
    primaryEstimateType: ESTIMATE_TYPES.TARGETED_PRODUCTS,
    secondaryEstimateType: ESTIMATE_TYPES.IMPRESSIONS,
    primaryIncreaseNullable: false,
    costEstimateType: ESTIMATE_TYPES.COST,
    isPrimaryFromToEnabled: true,
    visualType: VISUAL_TYPES.TABLE,
    visualTableScope: VISUAL_TABLE_ENABLED_SCOPES.CAMPAIGN,
    visualTableHeaders: [
      _TL_('Campaigns'),
      _TL_('Products targeted'),
      _TL_('Weekly est. impr.'),
    ],
    useNewI18n: true,
  },
  [RECOMMENDATION_TYPES.IMPROVE_MULTIMEDIA_ADS]: {
    summaryTitle: _TL_('Optimize your multimedia ads'),
    summaryIconClasses: 'recommendation-ads-icon',
    summaryIconClassesVNext: 'Chart',
    detailDescription: _TL_('Drive engagement with your multimedia ads by creating new ad texts, headlines, and images.'),
    detailPitching: _TL_('We\'ve recommended these changes for some of your multimedia ads because they have an insufficient number of ad texts, headlines, or images.'),
    detailHelpId: 'ext60107',
    primaryEstimateType: ESTIMATE_TYPES.CLICKS,
    secondaryEstimateType: ESTIMATE_TYPES.IMPRESSIONS,
    primaryIncreaseNullable: true,
    isPrimaryFromToEnabled: true,
    visualType: VISUAL_TYPES.ADS_PREVIEW,
    visualTableScope: VISUAL_TABLE_ENABLED_SCOPES.ADGROUP,
    visualTableHeaders: [
      _TL_('Ad preview'),
    ],
    useNewI18n: true,
  },
  [RECOMMENDATION_TYPES.SSC_2_PMAX_MIGRATION]: {
    summaryTitle: _TL_('Upgrade your smart shopping campaigns to Performance Max campaigns'),
    summaryIconClasses: 'recommendation-p-max-icon',
    summaryIconClassesVNext: 'Up',
    detailDescription: _TL_('Increase conversions by upgrading to Performance Max.'),
    detailPitching: _TL_('We\'re recommending this upgrade because you have smart shopping campaigns in this account.'),
    detailHelpId: 'ext60176',
    isPrimaryFromToEnabled: true,
    visualType: VISUAL_TYPES.TABLE,
    visualTableScope: VISUAL_TABLE_ENABLED_SCOPES.ADGROUP,
    visualTableHeaders: [
      _TL_('The following smart shopping campaigns are eligible to upgrade to Performance Max campaigns:'),
    ],
    useNewI18n: true,
  },
  [RECOMMENDATION_TYPES.PMAX_IMPORT]: {
    summaryTitle: _TL_('Import your Performance Max campaigns from Google Ads'),
    summaryIconClasses: 'recommendation-keyword-icon',
    summaryIconClassesVNext: 'SearchCampaigns',
    detailDescription: _TL_('Save time and effort by importing your Performance Max campaigns from Google Ads.'),
    detailPitching: _TL_('We recommend this because we now support Performance Max campaigns. Importing these campaigns can increase customer engagement and improve your ROI.'),
    detailHelpId: 'ext51050',
    isPrimaryFromToEnabled: true,
    visualType: VISUAL_TYPES.TABLE,
    visualTableHeaders: [],
    useNewI18n: true,
  },
  [RECOMMENDATION_TYPES.DSA_2_PMAX_MIGRATION]: {
    summaryTitle: _TL_('Upgrade your Dynamic Search Ad (DSA) campaigns to Performance Max campaigns'),
    summaryIconClasses: 'recommendation-p-max-icon',
    summaryIconClassesVNext: 'Up',
    detailDescription: _TL_('Upgrade your eligible DSA campaigns to Performance Max and we\'ll use your existing assets, settings, and budget to create a new Performance Max campaign.'),
    detailPitching: _TL_('We recommend this because Performance Max campaigns can generate 24% more conversions at the same cost per conversion.'),
    detailHelpId: '60366',
    visualType: VISUAL_TYPES.TABLE,
    visualTableHeaders: [],
    useNewI18n: true,
    primaryEstimateType: ESTIMATE_TYPES.CONVERSIONS,
    primaryIncreaseNullable: true,
    isPrimaryFromToEnabled: true,
  },
  [RECOMMENDATION_TYPES.SYNDICATION_GAP]: {
    summaryTitle: _TL_('Show ads on the entire Microsoft Advertising Network'),
    summaryIconClasses: 'recommendation-syndication-icon',
    summaryIconClassesVNext: 'Add',
    detailDescription: _TL_('Expand your advertising reach on the entire Microsoft Advertising Network.'),
    detailPitching: _TL_('Recommended because it can help you reach additional customers and drive conversions at a lower cost per click.'),
    detailHelpId: 'ext60146',
    visualType: VISUAL_TYPES.TABLE,
    useNewI18n: true,
    primaryEstimateType: ESTIMATE_TYPES.CLICKS,
    secondaryEstimateType: ESTIMATE_TYPES.CONVERSIONS,
    primaryIncreaseNullable: true,
    isPrimaryFromToEnabled: true,
    visualTableHeaders: [
      _TL_('Campaigns'),
      _TL_('Weekly est. clicks'),
      _TL_('Weekly est. conversions'),
    ],
  },
};

export const recommendationNewTextConfigs = {
  [RECOMMENDATION_TYPES.REMOVE_CONFLICTING_NEGATIVE_KEYWORD]: {
    permission: 'IsRemoveNegativeKeywordConflictNewTextsEnabled',
    configs: {
      detailDescription: 'Summary_Card_Detail_Description_Remove_Conflicting_Negative_Keyword_New',
      detailPitching: 'Summary_Card_Detail_Pitching_Remove_Conflicting_Negative_Keyword_New',
    },
    column: {
      BlockedKeywordsColumn: {
        helpId: 'pop_BA_Recommendations_BlockedKeywords',
      },
    },
  },
};

export const recommendationInProductUpdateConfigs = {
  [RECOMMENDATION_TYPES.REPAIR_ADS]: {
    permission: 'IsRecommendationInProductUpdateEnabled',
    available: 'isNrt',
    notification: 'Summary_Card_Repair_Opportunity_Update_Notification',
    additionalPitching: 'Detail_Card_Repair_Recommendations_NRT',
    additionalPitchingEnabledBy: 'available',
    helpTopic: 'pop_BA_Recommendations_RelevantSuggestions',
  },
  [RECOMMENDATION_TYPES.REPAIR_KEYWORD]: {
    permission: 'IsRecommendationInProductUpdateEnabled',
    available: 'isNrt',
    notification: 'Summary_Card_Repair_Opportunity_Update_Notification',
    additionalPitching: 'Detail_Card_Repair_Recommendations_NRT',
    additionalPitchingEnabledBy: 'available',
    helpTopic: 'pop_BA_Recommendations_RelevantSuggestions',
  },
  [RECOMMENDATION_TYPES.RESPONSIVE_SEARCH_ADS]: {
    permission: 'IsRSAAdoptionV2Enabled',
    available: 'hasAIAssetRecommendation',
    notification: 'Summary_Card_RSA_Opportunity_Update_Notification',
    additionalPitching: 'Detail_Card_RSA_Recommendations_Notification',
    additionalPitchingEnabledBy: 'permission',
    helpTopic: 'pop_BA_Recommendations_AI_HeadDescp',
  },
  [RECOMMENDATION_TYPES.BUDGET_OPPORTUNITY]: {
    permission: 'IsRecommendationInProductFeedbackLoopEnabled',
    available: 'isFeedbackPersonalized',
    notification: 'Summary_Card_Banner_AlertMsg_FeedbackPersonalized',
    additionalPitching: '',
    additionalPitchingEnabledBy: null,
    helpTopic: 'pop_BA_Recommendations_RelevantSuggestions',
  },
  [RECOMMENDATION_TYPES.SETUP_CONVERSION_TRACKING_OPPORTUNITY]: {
    permission: 'IsRecommendationThirdPartyTaggingEnabled',
    available: 'isNrt',
    notification: 'Summary_Card_SetupConversionTracking_Opportunity_Update_Notification',
    additionalPitching: '',
    additionalPitchingEnabledBy: null,
    helpTopic: null,
    needAdditionalArgs: true,
    neverExpired: true,
  },
  [RECOMMENDATION_TYPES.FIX_CONVERSION_TRACKING_OPPORTUNITY]: {
    permission: 'IsRecommendationThirdPartyTaggingEnabled',
    available: 'isNrt',
    notification: 'Summary_Card_SetupConversionTracking_Opportunity_Update_Notification',
    additionalPitching: '',
    additionalPitchingEnabledBy: null,
    helpTopic: null,
    needAdditionalArgs: true,
    neverExpired: true,
  },
};

export const recommendationDynamicTextConfigs = {
  [RECOMMENDATION_TYPES.SETUP_CONVERSION_TRACKING_OPPORTUNITY]: ({
    isMCC,
    statusId,
    recommendationsCount,
  }) => {
    const defaultConfig = {
      summaryTitle: 'Summary_Card_Title_Setup_Conversion_Tracking_Opportunity_UnCreated',
      detailDescription: 'Summary_Card_Detail_Description_Setup_Conversion_Tracking_Opportunity_UnCreated',
      detailPitching: 'Summary_Card_Detail_Pitching_Setup_Conversion_Tracking_Opportunity_UnCreated',
    };
    let newConfig;
    if (isMCC) {
      if (statusId === 2) {
        newConfig = {
          ...defaultConfig,
          summaryTitle: 'Summary_Card_Title_Setup_Conversion_Tracking_Opportunity_Unverified',
          detailDescription: `MCC_Summary_Card_Detail_Description_Setup_Conversion_Tracking_Opportunity_Unverified_${isPlural(recommendationsCount)}`,
          detailPitching: 'MCC_Summary_Card_Detail_Pitching_Setup_Conversion_Tracking_Opportunity',
        };
      } else {
        newConfig = {
          ...defaultConfig,
          summaryTitle: 'Summary_Card_Title_Setup_Conversion_Tracking_Opportunity_UnCreated',
          detailDescription: `MCC_Summary_Card_Detail_Description_Setup_Conversion_Tracking_Opportunity_UnCreated_${isPlural(recommendationsCount)}`,
          detailPitching: 'MCC_Summary_Card_Detail_Pitching_Setup_Conversion_Tracking_Opportunity',
        };
      }
    } else if (statusId === 2) {
      newConfig = {
        ...defaultConfig,
        summaryTitle: 'Summary_Card_Title_Setup_Conversion_Tracking_Opportunity_Unverified',
        detailDescription: 'Summary_Card_Detail_Description_Setup_Conversion_Tracking_Opportunity_Unverified',
        detailPitching: 'Summary_Card_Detail_Pitching_Setup_Conversion_Tracking_Opportunity_Unverified',
      };
    }
    return newConfig || defaultConfig;
  },
};

export const additionDetailPitchingConfig = {
  [RECOMMENDATION_TYPES.FIX_AD_DESTINATION]: {
    detailPitching: 'Detail_Page_Detail_Description_Fix_Ad_Destination_Opportunity',
  },
  [RECOMMENDATION_TYPES.FIX_AD_TEXT]: {
    detailPitching: 'Detail_Page_Detail_Description_Fix_Ad_Text_Opportunity',
  },
};
